import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline, Box, CircularProgress } from '@mui/material';
import { Layout } from './components/Layout/Layout';
import { RouteAnalytics } from './components/Analytics/RouteAnalytics';
import { getLightTheme } from './theme/getLightTheme';
import { CountryDataProvider } from './context/CountryDataContext';

// Each page is its own chunk — only downloaded when the user navigates to it.
const HomePage        = lazy(() => import('./components/HomePage/HomePage').then(m => ({ default: m.HomePage })));
const CountryDetails  = lazy(() => import('./components/CountryDetails/CountryDetails').then(m => ({ default: m.CountryDetails })));
const CurrencyConversion = lazy(() => import('./components/CurrencyConversion/CurrencyConversion').then(m => ({ default: m.CurrencyConversion })));
const LanguageLocale  = lazy(() => import('./components/LanguageLocale/LanguageLocale').then(m => ({ default: m.LanguageLocale })));
const HolidayCalendar = lazy(() => import('./components/HolidayCalendar/HolidayCalendar').then(m => ({ default: m.HolidayCalendar })));
const TimeZoneConverter = lazy(() => import('./components/TimeZoneConverter/TimeZoneConverter').then(m => ({ default: m.TimeZoneConverter })));
const IbanValidator   = lazy(() => import('./components/IbanValidator/IbanValidator').then(m => ({ default: m.IbanValidator })));
const UnitConverter   = lazy(() => import('./components/UnitConverter/UnitConverter').then(m => ({ default: m.UnitConverter })));
const HsCodeSearch    = lazy(() => import('./components/HsCodeSearch/HsCodeSearch').then(m => ({ default: m.HsCodeSearch })));
const IpGeolocation   = lazy(() => import('./components/IpGeolocation/IpGeolocation').then(m => ({ default: m.IpGeolocation })));
const DevComponents   = lazy(() => import('./components/DevComponents/DevComponents').then(m => ({ default: m.DevComponents })));
const ComingSoon      = lazy(() => import('./components/ComingSoon/ComingSoon').then(m => ({ default: m.ComingSoon })));
const NewsIndexPage   = lazy(() => import('./components/News/NewsIndexPage').then(m => ({ default: m.NewsIndexPage })));
const TopicPage       = lazy(() => import('./components/News/TopicPage').then(m => ({ default: m.TopicPage })));
const ArticlePage     = lazy(() => import('./components/News/ArticlePage').then(m => ({ default: m.ArticlePage })));
const ResourcesPage   = lazy(() => import('./components/Resources/ResourcesPage').then(m => ({ default: m.ResourcesPage })));
const AboutPage       = lazy(() => import('./components/About/AboutPage').then(m => ({ default: m.AboutPage })));
const PrivacyPolicy   = lazy(() => import('./components/Legal/PrivacyPolicy').then(m => ({ default: m.PrivacyPolicy })));
const Terms           = lazy(() => import('./components/Legal/Terms').then(m => ({ default: m.Terms })));
const VatCalculator   = lazy(() => import('./components/VatCalculator/VatCalculator').then(m => ({ default: m.VatCalculator })));
const PhoneNumberGenerator = lazy(() => import('./components/PhoneNumberGenerator/PhoneNumberGenerator').then(m => ({ default: m.PhoneNumberGenerator })));
const GlobalTradeTrends = lazy(() => import('./components/GlobalTradeTrends/GlobalTradeTrends').then(m => ({ default: m.GlobalTradeTrends })));

const PageLoader = () => (
  <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
    <CircularProgress />
  </Box>
);

// Reset scroll to the top on every route change — without this the router keeps
// the previous page's scroll offset when navigating between pages.
const ScrollToTop: React.FC = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const App: React.FC = () => {
  return (
    <ThemeProvider theme={getLightTheme}>
      <CssBaseline />
      <CountryDataProvider>
        <Router>
          <ScrollToTop />
          <RouteAnalytics />
          <Layout>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/"              element={<HomePage />} />
                <Route path="/details"       element={<CountryDetails />} />
                <Route path="/currency"      element={<CurrencyConversion />} />
                <Route path="/language-locale" element={<LanguageLocale />} />
                <Route path="/holidays"      element={<HolidayCalendar />} />
                <Route path="/timezone"      element={<TimeZoneConverter />} />
                <Route path="/iban"          element={<IbanValidator />} />
                <Route path="/unit-converter" element={<UnitConverter />} />
                <Route path="/hs-code"       element={<HsCodeSearch />} />
                <Route path="/geolocation"   element={<IpGeolocation />} />
                <Route path="/dev/components" element={<DevComponents />} />
                <Route path="/news"             element={<NewsIndexPage />} />
                <Route path="/news/topic/:topic" element={<TopicPage />} />
                <Route path="/news/:slug"       element={<ArticlePage />} />
                <Route path="/vat-calculator" element={<VatCalculator />} />
                <Route path="/phone-number-generator" element={<PhoneNumberGenerator />} />
                <Route path="/trade-trends" element={<GlobalTradeTrends />} />
                <Route path="/shipping-calculator" element={<ComingSoon title="Shipping Calculator" description="A worldwide shipping-cost estimator is on the roadmap and coming soon." />} />
                <Route path="/resources"     element={<ResourcesPage />} />
                <Route path="/about"         element={<AboutPage />} />
                <Route path="/privacy"       element={<PrivacyPolicy />} />
                <Route path="/terms"         element={<Terms />} />
              </Routes>
            </Suspense>
          </Layout>
        </Router>
      </CountryDataProvider>
    </ThemeProvider>
  );
};

export default App;
